import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Navbar from "../../components/Navbar/Navbar";
import "./DailyDipstick.css";

const DailyDipstick = () => {
  const today = new Date().toISOString().split("T")[0];

  const [targetDate, setTargetDate] = useState(today);
  const [force, setForce] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [jobs, setJobs] = useState([]);
  const [expandedJobLogs, setExpandedJobLogs] = useState({});

  const [reports, setReports] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total_pages: 1, total: 0 });
  const [page, setPage] = useState(1);

  const [detailReport, setDetailReport] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchJobs = useCallback(async () => {
    try {
      const res = await axios.get("/dipstick/jobs");
      setJobs(res.data);
    } catch (err) {
      console.error("Failed to fetch dipstick jobs:", err);
    }
  }, []);

  const fetchReports = useCallback(async (p) => {
    try {
      const res = await axios.get("/dipstick/reports", { params: { page: p, page_size: 20 } });
      setReports(res.data.data);
      setPagination(res.data.pagination);
    } catch (err) {
      console.error("Failed to fetch dipstick reports:", err);
    }
  }, []);

  useEffect(() => {
    fetchJobs();
    fetchReports(1);
  }, [fetchJobs, fetchReports]);

  useEffect(() => {
    const hasActive = jobs.some((j) => j.status === "running" || j.status === "pending");
    if (!hasActive) return;
    const interval = setInterval(() => {
      fetchJobs();
      fetchReports(page);
    }, 3000);
    return () => clearInterval(interval);
  }, [jobs, fetchJobs, fetchReports, page]);

  const isRunning = jobs.some((j) => j.status === "running" || j.status === "pending");

  const handleRun = async (dateStr) => {
    setError("");
    setSuccess("");
    if (!dateStr) {
      setError("Please select a date.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post("/dipstick/run", { date: dateStr, force });
      setSuccess(`Job ${res.data.job_id} started for ${dateStr}.`);
      await fetchJobs();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to start job.");
    } finally {
      setLoading(false);
    }
  };

  const handleRunToday = () => handleRun("today");
  const handleRunDate = () => handleRun(targetDate);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    fetchReports(newPage);
  };

  const handleViewReport = async (reportDate) => {
    setDetailLoading(true);
    setDetailReport(null);
    try {
      const res = await axios.get(`/dipstick/reports/${reportDate}`);
      setDetailReport(res.data);
    } catch (err) {
      console.error("Failed to fetch report:", err);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDeleteReport = async (reportDate) => {
    if (!window.confirm(`Delete report for ${reportDate}?`)) return;
    try {
      await axios.delete(`/dipstick/reports/${reportDate}`);
      fetchReports(page);
    } catch (err) {
      console.error("Failed to delete report:", err);
    }
  };

  const toggleJobLogs = (jobId) => {
    setExpandedJobLogs((prev) => ({ ...prev, [jobId]: !prev[jobId] }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      const d = new Date(dateStr + (dateStr.length === 10 ? "T00:00:00" : ""));
      return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
    } catch {
      return dateStr;
    }
  };

  return (
    <>
      <Navbar />
      <div className="dipstick-container">
        <div className="dipstick-header">
          <h1>Daily Dip Stick</h1>
          <p>Day-wise incident analysis from tickets and chat threads. Auto-runs daily at 5:00 PM.</p>
        </div>

        {isRunning && (
          <div className="dipstick-status-banner">
            <span className="dipstick-mini-spinner" />
            A dip stick job is running... Results will appear automatically.
          </div>
        )}

        {/* Run Controls */}
        <div className="dipstick-card">
          <h2>Generate Report</h2>
          <div className="dipstick-run-form">
            <div className="dipstick-date-field">
              <label>Date</label>
              <input
                type="date"
                value={targetDate}
                max={today}
                onChange={(e) => setTargetDate(e.target.value)}
              />
            </div>
            <button
              className="dipstick-run-btn"
              onClick={handleRunDate}
              disabled={loading || isRunning}
            >
              {loading ? "Starting..." : isRunning ? "Job Running..." : "Run for Date"}
            </button>
            <button
              className="dipstick-today-btn"
              onClick={handleRunToday}
              disabled={loading || isRunning}
            >
              Run Today
            </button>
            <label className="dipstick-force-toggle">
              <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} />
              Force regenerate
            </label>
          </div>
          {error && <p className="dipstick-error-msg">{error}</p>}
          {success && <p className="dipstick-success-msg">{success}</p>}
        </div>

        {/* Job History */}
        {jobs.length > 0 && (
          <div className="dipstick-card">
            <h2>Recent Jobs</h2>
            <table className="dipstick-jobs-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Progress</th>
                  <th>Items</th>
                  <th>Started</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job) => (
                  <React.Fragment key={job.id}>
                    <tr>
                      <td style={{ fontFamily: "monospace", fontSize: "0.85rem" }}>{job.id}</td>
                      <td>{job.target_date}</td>
                      <td>
                        <span className={`dipstick-status-badge dipstick-status-${job.status}`}>
                          {job.status}
                        </span>
                      </td>
                      <td style={{ minWidth: 120 }}>
                        <div className="dipstick-progress-bar-container">
                          <div
                            className={`dipstick-progress-bar-fill ${job.status}`}
                            style={{ width: `${job.progress || 0}%` }}
                          />
                        </div>
                      </td>
                      <td>{job.total_items ?? "—"}</td>
                      <td style={{ fontSize: "0.82rem", color: "#718096" }}>
                        {job.started_at ? new Date(job.started_at).toLocaleTimeString() : "—"}
                      </td>
                      <td>
                        <button className="dipstick-log-toggle-btn" onClick={() => toggleJobLogs(job.id)}>
                          {expandedJobLogs[job.id] ? "Hide Logs" : "Logs"}
                        </button>
                      </td>
                    </tr>
                    {expandedJobLogs[job.id] && job.logs && (
                      <tr>
                        <td colSpan={7} style={{ padding: 0 }}>
                          <div className="dipstick-log-panel">
                            {job.logs.map((line, i) => (
                              <p key={i}>{line}</p>
                            ))}
                            {job.logs.length === 0 && <p>No logs yet.</p>}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Reports List */}
        <div className="dipstick-card">
          <h2>Daily Reports</h2>
          {reports.length === 0 ? (
            <div className="dipstick-no-reports">
              No reports yet. Run a job to generate the first daily dip stick report.
            </div>
          ) : (
            <>
              <table className="dipstick-reports-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Tickets</th>
                    <th>Chats</th>
                    <th>Total</th>
                    <th>Summary</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {reports.map((r) => (
                    <tr key={r.id}>
                      <td>
                        <button className="dipstick-date-btn" onClick={() => handleViewReport(r.report_date)}>
                          {formatDate(r.report_date)}
                        </button>
                      </td>
                      <td><span className="dipstick-count-badge">{r.total_tickets}</span></td>
                      <td><span className="dipstick-count-badge">{r.total_chats}</span></td>
                      <td><span className="dipstick-count-badge">{r.total_items}</span></td>
                      <td>
                        <div className="dipstick-summary-preview" title={r.summary}>
                          {r.summary || "—"}
                        </div>
                      </td>
                      <td>
                        <button className="dipstick-delete-btn" onClick={() => handleDeleteReport(r.report_date)}>
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {pagination.total_pages > 1 && (
                <div className="dipstick-pagination">
                  <button
                    className="dipstick-page-btn"
                    disabled={page <= 1}
                    onClick={() => handlePageChange(page - 1)}
                  >
                    Previous
                  </button>
                  <span className="dipstick-page-info">
                    Page {pagination.page} of {pagination.total_pages}
                  </span>
                  <button
                    className="dipstick-page-btn"
                    disabled={page >= pagination.total_pages}
                    onClick={() => handlePageChange(page + 1)}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {(detailReport || detailLoading) && (
        <div className="dipstick-detail-overlay" onClick={() => { setDetailReport(null); setDetailLoading(false); }}>
          <div className="dipstick-detail-modal" onClick={(e) => e.stopPropagation()}>
            <button className="dipstick-detail-close-btn" onClick={() => { setDetailReport(null); setDetailLoading(false); }}>
              &times;
            </button>
            {detailLoading && !detailReport ? (
              <div className="dipstick-detail-loading">Loading report...</div>
            ) : detailReport ? (
              <>
                <div className="dipstick-detail-title">
                  Daily Dip Stick &mdash; {formatDate(detailReport.report_date)}
                </div>
                <div className="dipstick-detail-subtitle">
                  {detailReport.total_tickets} ticket(s), {detailReport.total_chats} chat thread(s) &mdash; {detailReport.total_items} total item(s)
                </div>

                <div className="dipstick-summary-box">{detailReport.summary}</div>

                {detailReport.items && detailReport.items.length > 0 ? (
                  <table className="dipstick-items-table">
                    <thead>
                      <tr>
                        <th>Source</th>
                        <th>Issue</th>
                        <th>Root Cause</th>
                        <th>Customer</th>
                        <th>Priority</th>
                        <th>Links</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detailReport.items.map((item) => (
                        <tr key={item.id}>
                          <td>
                            <span className={item.source_type === "ticket" ? "dipstick-badge-ticket" : "dipstick-badge-chat"}>
                              {item.source_type === "ticket" ? "Ticket" : "Chat"}
                            </span>
                          </td>
                          <td className="dipstick-title-cell">{item.title || "—"}</td>
                          <td className="dipstick-rca-cell">{item.root_cause || "—"}</td>
                          <td>{item.customer || "—"}</td>
                          <td>{item.priority || "—"}</td>
                          <td>
                            <div className="dipstick-links-cell">
                              {item.rca_doc_link && (
                                <a href={item.rca_doc_link} target="_blank" rel="noopener noreferrer" className="dipstick-link-btn dipstick-link-rca">
                                  RCA Doc
                                </a>
                              )}
                              {item.gchat_link && (
                                <a href={item.gchat_link} target="_blank" rel="noopener noreferrer" className="dipstick-link-btn dipstick-link-chat">
                                  Chat Thread
                                </a>
                              )}
                              {item.ticket_link && (
                                <a href={item.ticket_link} target="_blank" rel="noopener noreferrer" className="dipstick-link-btn dipstick-link-ticket">
                                  Ticket
                                </a>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="dipstick-empty-items">No incident items for this date.</div>
                )}
              </>
            ) : null}
          </div>
        </div>
      )}
    </>
  );
};

export default DailyDipstick;
